import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { waitForTick } from '../../shared/wait-for-tick';
import * as findWhere from 'lodash/find';
import * as isNaN from 'lodash/isNaN';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'townip-otp-code',
  templateUrl: './otp-code.component.html',
  styleUrl: './otp-code.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OtpCodeComponent),
      multi: true
    }
  ]
})
export class OtpCodeComponent implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor {

  @Input()
  public required: boolean;

  @Input()
  public minLength = 6;

  @Input()
  public forceInvalid = false;

  public currentValue: string;

  public otpCodeInput: string[] = new Array(this.minLength).fill(null);

  public onChange: any;

  public onTouched: any;

  public isTouched = false;

  public isInvalid = false;

  @ViewChildren(NgModel)
  private inputs: QueryList<NgModel>;

  @ViewChild('otpInput')
  private initialInput: ElementRef;

  private inputClassSubscription: Subscription;

  constructor(private elementRef: ElementRef) {

  }

  public ngOnInit(): void {
    this.inputClassSubscription = interval(200)
      .subscribe(() => {
        // this.isInvalid = this.elementRef.nativeElement.classList.contains('ng-invalid');
        // this.isTouched = this.elementRef.nativeElement.classList.contains('ng-touched');

        this.isInvalid = false;
        this.isTouched = false;

        if (this.forceInvalid) {
          this.isInvalid = true;
          this.isTouched = true;
        }
      });
  }

  public ngOnDestroy(): void {
    this.inputClassSubscription.unsubscribe();
  }

  public ngAfterViewInit(): void {
    this.initialInput.nativeElement.focus();
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public onInput(event: any): void {
    if (event.inputType === 'deleteContentBackward') {
      this.moveToPrev(event);
    } else if (event.inputType === 'insertText' || event.inputType === 'deleteContentForward') {
      this.moveToNext(event);
    }

    this.emitCode();
  }

  public onKeyDown(event: any): void {
    // Focus should still move to the previous input even if the current input has no value
    if (event.code === 'Backspace' && event.target.value.length === 0) {
      this.moveToPrev(event);
      event.preventDefault();
      return;
    }
  }

  public onPaste(event: any): void {
    const paste = event.clipboardData
      .getData('text')
      .toString();

    if (paste.length) {
      this.handleOnPaste(paste);
    }

    event.preventDefault();
  }

  public async handleOnPaste(paste: any): Promise<void> {
    const pastedCode = paste.substring(0, this.otpCodeInput.length + 1);

    if (isNaN(+pastedCode)) {
      return;
    }

    this.otpCodeInput.forEach((value, index) => {
      this.otpCodeInput[index] = pastedCode[index];
    });

    await waitForTick();

    this.emitCode();
  }

  private emitCode(): void {
    const controls = this.inputs.toArray();
    const invalid = findWhere(controls, { invalid: true });

    this.currentValue = invalid ? null : this.otpCodeInput.join('');

    if (this.onChange) {
      this.onChange(this.currentValue);
    }

    if (this.onTouched) {
      this.onTouched(true);
    }
  }

  private moveToPrev(event: InputEvent): void {
    const prevInput = this.findPrevInput(event.target);
    if (!prevInput) {
      return;
    }

    prevInput.focus();
    prevInput.select();
  }

  private moveToNext(event: InputEvent): void {
    const nextInput = this.findNextInput(event.target);
    if (!nextInput) {
      return;
    }

    nextInput.focus();
    nextInput.select();
  }

  private findNextInput(element: any): any {
    const nextElement = element.nextElementSibling;
    if (!nextElement) {
      return;
    }

    return nextElement.nodeName === 'INPUT' ? nextElement : this.findNextInput(nextElement);
  }

  private findPrevInput(element: any): any {
    const prevElement = element.previousElementSibling;
    if (!prevElement) {
      return;
    }

    return prevElement.nodeName === 'INPUT' ? prevElement : this.findPrevInput(prevElement);
  }

  public writeValue(value: any): void {
    this.currentValue = value;
  }

  public registerOnChange(func: any): void {
    this.onChange = func;
  }

  public registerOnTouched(func: any): void {
    this.onTouched = func;
  }

}
